
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Mail, Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { logError } from '@/lib/errorLogger';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const TestEmailsTab = () => {
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [activeType, setActiveType] = useState<'application_received' | 'application_approved' | null>(null);
  const [apiResponse, setApiResponse] = useState<any>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const { toast } = useToast();

  const sendTestEmail = async (type: 'application_received' | 'application_approved') => {
    if (!email) {
      toast({
        title: "E-Mail-Adresse erforderlich",
        description: "Bitte geben Sie eine E-Mail-Adresse ein.",
        variant: "destructive"
      });
      return;
    }

    setIsSending(true);
    setActiveType(type);
    setApiResponse(null);
    setApiError(null);
    
    try {
      console.log(`Sending test email of type "${type}" to ${email}`);
      
      const { data, error } = await supabase.functions.invoke('beta-notification', {
        body: {
          type,
          data: {
            name: "Max Mustermann",
            email: email,
            ...(type === 'application_approved' && { inviteCode: "TEST-123-456" })
          }
        }
      });

      if (error) {
        console.error('Error from edge function:', error);
        setApiError(`Edge function error: ${error.message}`);
        setApiResponse({ error });
        throw new Error(`Edge function error: ${error.message}`);
      }

      console.log('Beta notification response:', data);
      setApiResponse(data);

      toast({
        title: "E-Mail-Anfrage gesendet",
        description: `Test-E-Mail vom Typ "${type}" wurde verarbeitet. Bitte prüfen Sie die Edge-Function-Logs und Ihren E-Mail-Eingang.`,
      });
    } catch (error: any) {
      console.error('Error sending test email:', error);
      logError(error, 'TestEmailsTab');
      setApiError(error.message || 'Unbekannter Fehler');
      
      toast({
        title: "Fehler beim Senden der E-Mail",
        description: `Fehler: ${error.message || 'Unbekannter Fehler'}`,
        variant: "destructive"
      });
    } finally {
      setIsSending(false);
      setActiveType(null);
    }
  };

  const checkResendApiKey = async () => {
    try {
      setIsSending(true);
      setApiError(null);
      const { data, error } = await supabase.functions.invoke('beta-notification', {
        body: {
          action: 'check_api_key'
        }
      });

      if (error) throw error;
      
      toast({
        title: "API-Schlüssel-Status",
        description: data.message || "Überprüfung abgeschlossen.",
      });
      
      setApiResponse(data);
    } catch (error: any) {
      console.error('Error checking API key:', error);
      setApiError(error.message || 'Unbekannter Fehler');
      toast({
        title: "Fehler",
        description: `API-Schlüssel-Überprüfung fehlgeschlagen: ${error.message || 'Unbekannter Fehler'}`,
        variant: "destructive"
      });
      setApiResponse({ error: error.message });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>E-Mail-Templates testen</CardTitle>
        <CardDescription>
          Testen Sie die E-Mail-Templates für Beta-Bewerbungen. Die E-Mails werden tatsächlich versendet.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="email">Test-E-Mail-Adresse</Label>
          <Input
            id="email"
            type="email"
            placeholder="ihre@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {apiError && (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4 mr-2" />
            <AlertTitle>Fehler aufgetreten</AlertTitle>
            <AlertDescription>
              {apiError}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <Button
            onClick={() => sendTestEmail('application_received')}
            disabled={isSending}
            className="w-full"
          >
            {isSending && activeType === 'application_received' ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Mail className="mr-2 h-4 w-4" />
            )}
            Bewerbungseingang testen
          </Button>

          <Button
            onClick={() => sendTestEmail('application_approved')}
            disabled={isSending}
            className="w-full"
          >
            {isSending && activeType === 'application_approved' ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Mail className="mr-2 h-4 w-4" />
            )}
            Einladungs-E-Mail testen
          </Button>
        </div>
        
        <div className="pt-4 border-t border-border">
          <CardTitle className="text-lg mb-2">Diagnose-Tools</CardTitle>
          <Button
            onClick={checkResendApiKey}
            disabled={isSending}
            variant="outline"
          >
            {isSending ? (
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Resend API-Schlüssel prüfen
          </Button>
        </div>
        
        {apiResponse && (
          <div className="mt-4 p-4 bg-muted/30 rounded-md">
            <h4 className="text-sm font-medium mb-2">API-Antwort:</h4>
            <pre className="text-xs overflow-auto p-2 bg-muted rounded">
              {JSON.stringify(apiResponse, null, 2)}
            </pre>
          </div>
        )}

        <Alert>
          <AlertDescription>
            <strong>Hinweis:</strong> Falls keine E-Mails ankommen, überprüfen Sie bitte:
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Ob der Resend API-Schlüssel korrekt eingerichtet ist</li>
              <li>Ob die Domain "vibe-mail.com" bei Resend verifiziert ist</li>
              <li>Die Edge-Function-Logs für Fehlermeldungen</li>
              <li>Ob die E-Mail möglicherweise im Spam-Ordner gelandet ist</li>
            </ul>
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
};

export default TestEmailsTab;
